import { __decorate, __awaiter } from 'tslib';
import 'long';
import { im_proto } from '../proto/index.js';
import { ErrorType } from '../core/consts.js';
import '../core/metrics/logger.js';
import { BaseError } from '../core/errors/BaseError.js';
import '../core/metrics/monitor.js';
import '../core/network/NetworkItem.js';
import { ContextClassBase, ServiceIdentity } from '../core/service/RuntimeContext.js';
import 'json-long';
import 'moize';
import '../core/model/Message.js';
import 'js-md5';
import '../core/utils/lz4.js';
import '../core/utils/serializer.js';
import '../adapter/manager/AdapterManager.js';
import '../adapter/manager/types.js';
import '../core/utils/ticker.js';
import '../adapter/abstract/IMNetApi.js';
import '../core/options.js';
import '../core/utils/uuid.js';
import '../core/service/ConversationManager.js';
import '../core/service/MessageManager.js';
import '@byted/uc-secure-sdk';
import '../core/plugin/DbProxy.js';
import { TrackerItem } from '../core/metrics/trackerTypes.js';
import '../core/model/CommandMessage.js';
import { RtcEventBus } from './RtcEvent.js';
import { RtcMetrics } from './RtcMetrics.js';

class RtcController extends ContextClassBase {
    constructor(ctx, apiManager, target) {
        super(ctx);
        this.target = target;
        this.__internal_event = new RtcEventBus(this.ctx);
        this.api = apiManager;
        this.manager.setEvent(this.__internal_event);
    }
    // 生成 幂等id, 单聊使用对方 id, 多人 join uid, 群聊使用 short id
    static genIdeomptentId(target) {
        if (typeof target === 'string') {
            return target;
        }
        else if (Array.isArray(target)) {
            return [...target].sort().join(':');
        }
        else {
            return target.shortId;
        }
    }
    get idempotentId() {
        return RtcController.genIdeomptentId(this.target);
    }
    genType(type) {
        let audio = true;
        let video = true;
        switch (type) {
            case im_proto.VoipType.VOIP_TYPE_AUDIOONLY:
                video = false;
                break;
            case im_proto.VoipType.VOIP_TYPE_VIDEOONLY:
                audio = false;
                break;
        }
        return [audio, video];
    }
    get event() {
        return this.__internal_event;
    }
    get chatInfo() {
        const chatInfo = this.manager.info;
        if (chatInfo) {
            this.cachedChatInfo = chatInfo;
        }
        return this.cachedChatInfo;
    }
    get sdk() {
        return this.manager.rtcApi;
    }
    get client() {
        return this.manager.client;
    }
    get manager() {
        return this.resolve(ServiceIdentity.RtcManager);
    }
    get participants() {
        return Array.from(this.chatInfo.participant.values());
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.reset();
            this.applyEvent();
        });
    }
    applyEvent() {
        return this.manager.setEvent(this.__internal_event);
    }
    reset() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.manager.reset(this.api);
        });
    }
    startCall(type = im_proto.VoipType.VOIP_TYPE_ALL, audioInput, videoInput) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return __awaiter(this, void 0, void 0, function* () {
            let [audio, video] = this.genType(type);
            if (audio && audioInput) {
                audio = audioInput;
            }
            if (video && videoInput) {
                video = videoInput;
            }
            this.manager.setEvent(this.__internal_event);
            const result = yield this.manager.startCall(type, RtcController.genIdeomptentId(this.target), this.target);
            this.resolve(ServiceIdentity.Monitor).emitTracker(TrackerItem.RtcStartCall, {
                imsdk_result: result.success ? '1' : '0',
                logid: (_a = result.payload) === null || _a === void 0 ? void 0 : _a.channelId,
                error_code: result.success ? '' : (_b = result.checkCode) === null || _b === void 0 ? void 0 : _b.toString(),
                error_msg: result.success ? '' : result.checkMsg,
                conversation_id: (_d = (_c = result.payload) === null || _c === void 0 ? void 0 : _c.conversationShortId) !== null && _d !== void 0 ? _d : '',
                is_multi_type: Object.keys((_f = (_e = result.payload) === null || _e === void 0 ? void 0 : _e.participant) !== null && _f !== void 0 ? _f : {}).length > 2 ? 1 : 0,
                is_video: video ? 1 : 0,
                user_count: Object.keys((_h = (_g = result.payload) === null || _g === void 0 ? void 0 : _g.participant) !== null && _h !== void 0 ? _h : {}).length
            });
            if (result.success) {
                yield this.manager.createStream(audio, video);
                yield this.manager.publish();
            }
            return result;
        });
    }
    cancelCall() {
        return __awaiter(this, void 0, void 0, function* () {
            this.applyEvent();
            yield this.manager.cancelCall();
        });
    }
    endCall() {
        return __awaiter(this, void 0, void 0, function* () {
            this.applyEvent();
            yield this.manager.terminate(true);
        });
    }
    acceptCall(type) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!type) {
                type = (_a = this.chatInfo) === null || _a === void 0 ? void 0 : _a.voipType;
            }
            const [audio, video] = this.genType(type);
            this.applyEvent();
            yield this.manager.acceptCall(type);
            yield this.manager.createStream(audio, video);
            yield this.manager.publish();
        });
    }
    denyCall() {
        return __awaiter(this, void 0, void 0, function* () {
            this.applyEvent();
            return this.manager.denyCall();
        });
    }
    getDevices() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.manager.getDevices();
        });
    }
    playToDom(uid, domId, fit) {
        if (!this.chatInfo.participant.has(uid)) {
            throw new BaseError({
                ctx: this.ctx,
                type: ErrorType.RtcInvalidParam,
                sender: this,
                msg: `participant: ${uid} not exist in local`
            });
        }
        const participant = this.chatInfo.participant.get(uid);
        if (!participant.stream) {
            throw new BaseError({
                ctx: this.ctx,
                type: ErrorType.RtcInvalidParam,
                sender: this,
                msg: `no participant stream provided for user:${uid}`
            });
        }
        void participant.stream.play(domId, {
            fit
        });
    }
    toggleVideo() {
        return this.manager.toggleVideo();
    }
    toggleAudio() {
        return this.manager.toggleAudio();
    }
    switchCamera() {
        return this.manager.switchCamera();
    }
    getLocalVideoStats() {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this.manager.getLocalVideoStats();
            for (const val of Object.values(result)) {
                return val;
            }
        });
    }
    getLocalAudioStats() {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this.manager.getLocalAudioStats();
            for (const val of Object.values(result)) {
                return val;
            }
        });
    }
    invite(uid) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.manager.invite(uid);
        });
    }
    getRemoteVideoStats() {
        return this.manager.getRemoteVideoStats();
    }
    getRemoteAudioStats() {
        return this.manager.getRemoteAudioStats();
    }
}
__decorate([
    RtcMetrics
], RtcController.prototype, "startCall", null);
__decorate([
    RtcMetrics
], RtcController.prototype, "cancelCall", null);
__decorate([
    RtcMetrics
], RtcController.prototype, "endCall", null);
__decorate([
    RtcMetrics
], RtcController.prototype, "acceptCall", null);
__decorate([
    RtcMetrics
], RtcController.prototype, "denyCall", null);
__decorate([
    RtcMetrics
], RtcController.prototype, "getDevices", null);
__decorate([
    RtcMetrics
], RtcController.prototype, "getLocalVideoStats", null);
__decorate([
    RtcMetrics
], RtcController.prototype, "getLocalAudioStats", null);
__decorate([
    RtcMetrics
], RtcController.prototype, "invite", null);
__decorate([
    RtcMetrics
], RtcController.prototype, "getRemoteVideoStats", null);
__decorate([
    RtcMetrics
], RtcController.prototype, "getRemoteAudioStats", null);

export { RtcController };
//# sourceMappingURL=RtcController.js.map
